import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Autocomplete,
} from "@mui/material";
import "./feedbackViewModal.css";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  Assignee,
  ViewSubmitRequestData,
} from "../../../features/manageFeedbacks/manageFeedbacksTypes";
import { getSupportAssigneeFromID } from "../../../features/manageFeedbacks/manageFeedbacksService";
import {
  TranslationEnum,
  usePortalTranslation,
} from "../../../providers/TranslationProvider";

interface FeedbackViewModalProps {
  open: boolean;
  onClose: () => void;
  feedbackData: Record<string, any>;
  brandsData: string[];
  flavoursData: string[];
  onConfirm: (data: ViewSubmitRequestData) => void;
  manufacturingLocations: string[];
  supportAssignees: Assignee[];
}

const FeedbackViewModal: React.FC<FeedbackViewModalProps> = ({
  open,
  onClose,
  feedbackData,
  brandsData,
  flavoursData,
  onConfirm,
  manufacturingLocations,
  supportAssignees,
}) => {
  const { translate } = usePortalTranslation();

  const feedbackDetails = {
    tokenId: feedbackData?.support_id || "",
    name: feedbackData?.extended_attributes?.userDetails?.name || "",
    updated: feedbackData?.last_modified_time || "",
    feedbackType: feedbackData?.support_class || "",
    channel: feedbackData?.extended_attributes?.channel || "",
    complaintType: feedbackData?.extended_attributes?.feedbackType || "",
    concernType: feedbackData?.extended_attributes?.concern || "",
    status: feedbackData?.status || "",
    startDate: feedbackData?.creation_time || "",
    city: feedbackData?.town || "",
    assignUser:
      getSupportAssigneeFromID(supportAssignees, feedbackData?.immediate_id)
        ?.name || "",
  };
  const [isSubmitAttempted, setIsSubmitAttempted] = useState(false);
  // State for the input fields
  const [brand, setBrand] = useState(
    feedbackData.extended_attributes?.brand || ""
  );
  const [flavour, setFlavour] = useState(
    feedbackData.extended_attributes?.flavour || ""
  );
  const [pfgDate, setPfgDate] = useState<Dayjs | null>(
    feedbackData?.extended_attributes?.mfgDate
      ? dayjs(feedbackData.extended_attributes.mfgDate, "DD-MM-YYYY")
      : null
  );
  const [batchNo, setBatchNo] = useState(
    feedbackData.extended_attributes?.batchNo || ""
  );
  const [packNo, setPackNo] = useState(
    feedbackData.extended_attributes?.content || ""
  );
  const [manufacturingLocation, setManufacturingLocation] = useState(
    feedbackData.extended_attributes?.manufacturingLocation || ""
  );
  const [remarks, setRemarks] = useState(feedbackData.remarks || "");

  // Error states
  const [brandError, setBrandError] = useState(
    feedbackData?.extended_attributes?.brand ? false : true
  );
  const [flavourError, setFlavourError] = useState(
    feedbackData?.extended_attributes?.flavour ? false : true
  );
  const [pfgDateError, setPfgDateError] = useState(
    feedbackData?.extended_attributes?.mfgDate ? false : true
  );
  const [batchNoError, setBatchNoError] = useState(
    feedbackData?.extended_attributes?.batchNo ? false : true
  );
  const [packNoError, setPackNoError] = useState(
    feedbackData?.extended_attributes?.content ? false : true
  );
  const [manufacturingLocationError, setManufacturingLocationError] = useState(
    feedbackData?.extended_attributes?.manufacturingLocation ? false : true
  );

  const validateInputs = () => {
    return (
      !packNoError &&
      !brandError &&
      !flavourError &&
      !pfgDateError &&
      !batchNoError &&
      !manufacturingLocationError
    );
  };

  const handleSubmit = () => {
    setIsSubmitAttempted(true);
    if (validateInputs()) {
      const data: ViewSubmitRequestData = {
        extendedAttributes: {
          city: feedbackData.town,
          brand: brand,
          batchNo: batchNo,
          channel: feedbackData.extended_attributes?.channel,
          concern: feedbackData.extended_attributes?.concern,
          content: packNo,
          flavour: flavour,
          mfgDate: pfgDate ? pfgDate.format("DD-MM-YYYY") : "",
          blobKey1: feedbackData?.blobKey,
          complaint: feedbackData?.extended_attributes?.complaint,
          noOfUnits: feedbackData?.extended_attributes?.noOfUnits,
          userDetails: feedbackData?.extended_attributes?.userDetails,
          feedbackType: feedbackData?.extended_attributes?.feedbackType,
          materialName: feedbackData?.extended_attributes?.materialName,
          manufacturingLocation: manufacturingLocation,
        },
        remarks: remarks,
        supportId: feedbackData.support_id,
      };
      onConfirm(data);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>
        {translate(TranslationEnum.manage_feedbacks, "View Feedback Details")}
      </DialogTitle>
      <DialogContent className="feedback-viewModal-mainContent-wrapper">
        <Typography
          variant="subtitle1"
          className="feedback-viewModal-info-text-wrapper"
        >
          <div className="feedback-viewModal-info-text-label">
            {translate(TranslationEnum.manage_feedbacks, "Token Id:")}
          </div>
          <div className="feedback-viewModal-info-text-value">
            {feedbackDetails.tokenId}
          </div>
        </Typography>
        <Typography
          variant="subtitle1"
          className="feedback-viewModal-info-text-wrapper"
        >
          <div className="feedback-viewModal-info-text-label">
            {translate(TranslationEnum.manage_feedbacks, "Name:")}
          </div>
          <div className="feedback-viewModal-info-text-value">
            {feedbackDetails.name}
          </div>
        </Typography>
        <Typography
          variant="subtitle1"
          className="feedback-viewModal-info-text-wrapper"
        >
          <div className="feedback-viewModal-info-text-label">
            {translate(TranslationEnum.manage_feedbacks, "Updated:")}
          </div>
          <div className="feedback-viewModal-info-text-value">
            {feedbackDetails.updated}
          </div>
        </Typography>
        <Typography
          variant="subtitle1"
          className="feedback-viewModal-info-text-wrapper"
        >
          <div className="feedback-viewModal-info-text-label">
            {translate(TranslationEnum.manage_feedbacks, "Feedback Type:")}
          </div>
          <div className="feedback-viewModal-info-text-value">
            {feedbackDetails.feedbackType}
          </div>
        </Typography>
        <Typography
          variant="subtitle1"
          className="feedback-viewModal-info-text-wrapper"
        >
          <div className="feedback-viewModal-info-text-label">
            {translate(TranslationEnum.manage_feedbacks, "Channel:")}
          </div>
          <div className="feedback-viewModal-info-text-value">
            {feedbackDetails.channel}
          </div>
        </Typography>
        <Typography
          variant="subtitle1"
          className="feedback-viewModal-info-text-wrapper"
        >
          <div className="feedback-viewModal-info-text-label">
            {translate(TranslationEnum.manage_feedbacks, "Do you have:")}
          </div>
          <div className="feedback-viewModal-info-text-value">
            {feedbackDetails.complaintType}
          </div>
        </Typography>
        <Typography
          variant="subtitle1"
          className="feedback-viewModal-info-text-wrapper"
        >
          <div className="feedback-viewModal-info-text-label">
            {translate(TranslationEnum.manage_feedbacks, "Concern Type :")}
          </div>
          <div className="feedback-viewModal-info-text-value">
            {feedbackDetails.concernType}
          </div>
        </Typography>
        <Autocomplete
          sx={{ width: "100%" }}
          value={brand || null} // Use null if empty
          disableClearable
          onChange={(event, newValue) => {
            setBrandError(newValue ? false : true);
            setBrand(newValue || "");
          }}
          options={brandsData} // Add more options as needed
          renderInput={(params) => (
            <TextField
              {...params}
              label={translate(TranslationEnum.manage_feedbacks, "Brand")}
              error={brandError && isSubmitAttempted}
              helperText={
                brandError && isSubmitAttempted
                  ? translate(
                      TranslationEnum.manage_feedbacks,
                      "Brand is required."
                    )
                  : ""
              }
            />
          )}
        />

        <Autocomplete
          sx={{ width: "100%" }}
          value={flavour || null} // Use null if empty
          disableClearable
          onChange={(event, newValue) => {
            setFlavourError(newValue ? false : true);
            setFlavour(newValue || "");
          }}
          options={flavoursData} // Add more options as needed
          renderInput={(params) => (
            <TextField
              {...params}
              label={translate(TranslationEnum.manage_feedbacks, "Flavour")}
              error={flavourError && isSubmitAttempted}
              helperText={
                flavourError && isSubmitAttempted
                  ? translate(
                      TranslationEnum.manage_feedbacks,
                      "Flavour is required."
                    )
                  : ""
              }
            />
          )}
        />
        <TextField
          label="Pack"
          value={packNo}
          variant="outlined"
          fullWidth
          onChange={(e) => {
            setPackNoError(e.target.value ? false : true);
            setPackNo(e.target.value);
          }}
          error={packNoError && isSubmitAttempted}
          helperText={
            packNoError && isSubmitAttempted
              ? translate(
                  TranslationEnum.manage_feedbacks,
                  "Pack No is required."
                )
              : ""
          }
        />

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label={translate(
              TranslationEnum.manage_feedbacks,
              "Manufacturing Date"
            )}
            inputFormat="DD-MM-YYYY"
            value={pfgDate}
            maxDate={dayjs()}
            onChange={(value) => {
              setPfgDateError(value ? false : true);
              setPfgDate(value);
            }}
            renderInput={(props) => (
              <TextField
                {...props}
                size="small"
                fullWidth
                error={pfgDateError && isSubmitAttempted}
                helperText={
                  pfgDateError && isSubmitAttempted
                    ? translate(
                        TranslationEnum.manage_feedbacks,
                        "PFG Date is required."
                      )
                    : ""
                }
              />
            )}
          />
        </LocalizationProvider>

        <TextField
          label={translate(TranslationEnum.manage_feedbacks, "Batch No")}
          value={batchNo}
          variant="outlined"
          fullWidth
          onChange={(e) => {
            setBatchNoError(e.target.value ? false : true);
            setBatchNo(e.target.value);
          }}
          error={batchNoError && isSubmitAttempted}
          helperText={
            batchNoError && isSubmitAttempted
              ? translate(
                  TranslationEnum.manage_feedbacks,
                  "Batch No is required."
                )
              : ""
          }
        />

        <Autocomplete
          sx={{ width: "100%" }}
          value={manufacturingLocation || null} // Use null if empty
          disableClearable
          onChange={(event, newValue) => {
            setManufacturingLocationError(newValue ? false : true);
            setManufacturingLocation(newValue || "");
          }}
          options={manufacturingLocations} // Add actual options here
          renderInput={(params) => (
            <TextField
              {...params}
              label={translate(
                TranslationEnum.manage_feedbacks,
                "Manufacturing Location"
              )}
              error={manufacturingLocationError && isSubmitAttempted}
              helperText={
                manufacturingLocationError && isSubmitAttempted
                  ? translate(
                      TranslationEnum.manage_feedbacks,
                      "Manufacturing Location is required."
                    )
                  : ""
              }
            />
          )}
        />

        <TextField
          label={translate(TranslationEnum.manage_feedbacks, "Remarks")}
          value={remarks}
          variant="outlined"
          fullWidth
          multiline
          rows={4}
          onChange={(e) => {
            setRemarks(e.target.value);
          }}
        />
        <Typography
          variant="subtitle1"
          className="feedback-viewModal-info-text-wrapper"
        >
          <div className="feedback-viewModal-info-text-label">
            {translate(TranslationEnum.manage_feedbacks, "Assign User:")}
          </div>
          <div className="feedback-viewModal-info-text-value">
            {feedbackDetails.assignUser}
          </div>
        </Typography>
        <Typography
          variant="subtitle1"
          className="feedback-viewModal-info-text-wrapper"
        >
          <div className="feedback-viewModal-info-text-label">
            {translate(TranslationEnum.manage_feedbacks, "Status:")}
          </div>
          <div className="feedback-viewModal-info-text-value">
            {feedbackDetails.status}
          </div>
        </Typography>
        <Typography
          variant="subtitle1"
          className="feedback-viewModal-info-text-wrapper"
        >
          <div className="feedback-viewModal-info-text-label">
            {translate(TranslationEnum.manage_feedbacks, "Start Date:")}
          </div>
          <div className="feedback-viewModal-info-text-value">
            {feedbackDetails.startDate}
          </div>
        </Typography>
        <Typography
          variant="subtitle1"
          className="feedback-viewModal-info-text-wrapper"
        >
          <div className="feedback-viewModal-info-text-label">
            {translate(TranslationEnum.manage_feedbacks, "City:")}
          </div>
          <div className="feedback-viewModal-info-text-value">
            {feedbackDetails.city}
          </div>
        </Typography>
      </DialogContent>
      <DialogActions>
        <div
          onClick={onClose}
          className="manageFeedback-cancel-button mangageFeedbacks-modalCancelButton"
        >
          {translate(TranslationEnum.common_portal, "Close")}
        </div>
        <div
          onClick={() => {
            handleSubmit();
          }}
          className="mangageFeedbacks-modalSubmitButton 
              manageFeedbacks-active-button"
        >
          {translate(TranslationEnum.common_portal, "Submit")}
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackViewModal;
